import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "store/actions";
import { State } from "store/reducers";
import Report from "Components/Report";
import ls from "Localization";
import { toStringCurrency } from "helpers/string";
import { InvoiceState } from "store/reducers/invoice";
import ReactTable from "react-table";
import _ from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { CNPJ } from "Components/NumberFormat";
import { InvoiceAggregationStatus } from "Types/InvoiceAggregation";
import { isInRole } from "helpers/auth";
import * as api from "services/api";
import IMei from "Types/IMei";

dayjs.extend(utc);

const MONTHLY_CAP_REAIS = 6750;
const ANNUAL_CAP_REAIS = 81000;

function parseMeiOpeningDate(
	openingDate: string | undefined
): { month: number; year: number } | null {
	if (!openingDate) return null;
	try {
		if (/^\d+$/.test(openingDate)) {
			const d = new Date(parseInt(openingDate, 10));
			if (Number.isNaN(d.getTime())) return null;
			return { month: d.getMonth() + 1, year: d.getFullYear() };
		}
		const parts = openingDate.split("/");
		if (parts.length < 3) return null;
		const month = parseInt(parts[1], 10);
		const year = parseInt(parts[2], 10);
		if (Number.isNaN(month) || Number.isNaN(year)) return null;
		return { month, year };
	} catch {
		return null;
	}
}

/**
 * Base MEI revenue cap (R$) for the aggregation calendar year, using R$ 6.750 per month
 * the MEI was active in that year (aligned with statusCalculator / report rules).
 */
function meiBaseLimitReaisForRowYear(
	rowYearStr: string,
	openingDate: string | undefined,
	currentYear: number,
	currentMonth: number
): number {
	const rowYear = parseInt(rowYearStr, 10);
	if (Number.isNaN(rowYear)) {
		return ANNUAL_CAP_REAIS;
	}

	const opening = parseMeiOpeningDate(openingDate);

	if (opening && opening.year > rowYear) {
		return 0;
	}

	const openingYear = opening?.year;
	const openingMonth = opening?.month;

	if (rowYear < currentYear) {
		if (!opening) {
			return ANNUAL_CAP_REAIS;
		}
		if (openingYear! < rowYear) {
			return ANNUAL_CAP_REAIS;
		}
		const om = openingMonth!;
		if (om < 1 || om > 12) {
			return ANNUAL_CAP_REAIS;
		}
		const monthsInYear = Math.max(1, 12 - om + 1);
		return monthsInYear * MONTHLY_CAP_REAIS;
	}

	if (rowYear > currentYear) {
		return 0;
	}

	if (!opening) {
		return currentMonth * MONTHLY_CAP_REAIS;
	}

	const oy = openingYear!;
	const om = openingMonth!;

	if (oy < currentYear) {
		return currentMonth * MONTHLY_CAP_REAIS;
	}

	return Math.max(1, currentMonth - om + 1) * MONTHLY_CAP_REAIS;
}

let getInvoicesDebounced: (() => any) & _.Cancelable;

function Invoicing() {
	const dispatch = useDispatch<any>();
	const table = useRef<ReactTable<any>>(null);

	const state = useSelector<State, InvoiceState>(s => s.invoice);
	const { user, token } = useSelector<State, any>(s => s.login);
	const [logisticOperators, setLogisticOperators] = useState<
		{ id: string; label: string }[]
	>([]);

	useEffect(() => {
		if (!isInRole(user, ["admin"]) || !token) return;

		let cancelled = false;
		(async () => {
			try {
				const response = await api.sendGet("/Invoice/logistic-operators", {
					Authorization: `Bearer ${token}`
				});
				const result = await response.json();
				if (!cancelled && result.isValid && Array.isArray(result.model)) {
					setLogisticOperators(result.model);
				}
			} catch {
				if (!cancelled) setLogisticOperators([]);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [user, token]);

	const handleFetchData = (tableState: any) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;
		let { sorted, filtered } = tableState;

		if (getInvoicesDebounced) {
			getInvoicesDebounced.cancel();
		}

		if (!sorted) sorted = [];

		filtered = [...(filtered || [])];

		getInvoicesDebounced = _.debounce(
			() =>
				dispatch(
					actions.invoice.getInvoicing(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getInvoicesDebounced();
	};

	const columns = useMemo(() => {
		const cols: any[] = [
				{
					Header: "MEI",
					id: "mei",
					show: true,
					accessor: (c: any) =>
						c.mei?.businessName || c.mei?.personName || ls.notDefined
				},
				{
					Header: "CNPJ",
					id: "mei.cnpj",
					show: true,
					format: c => c.mei.cnpj,
					accessor: (c: any) => <CNPJ value={c.mei.cnpj} displayType="text" />,
					width: 180
				}
		];

		if (isInRole(user, ["admin"])) {
			cols.push({
				Header: ls.logisticOperator,
				id: "logisticOperator",
				show: true,
				accessor: (c: any) => {
					const op = (c.mei as IMei)?.logisticOperator as any;
					if (!op) return ls.notInformed;
					if (typeof op === "object") {
						return op.businessName || op.personName || ls.notDefined;
					}
					return ls.notInformed;
				},
				exportAccessor: (c: any) => {
					const op = (c.mei as IMei)?.logisticOperator as any;
					if (op && typeof op === "object") {
						return op.businessName || op.personName || "";
					}
					return "";
				},
				width: 200,
				Filter: ({ filter, onChange }: any) => (
					<select
						onChange={e => onChange(e.target.value)}
						style={{ width: "100%" }}
						value={filter?.value ?? ""}
					>
						<option value="">Todos</option>
						{logisticOperators.map(op => (
							<option key={op.id} value={op.id}>
								{op.label}
							</option>
						))}
					</select>
				)
			});
		}

		cols.push(
				{
					Header: "Ano",
					id: "year",
					show: true,
					accessor: (c: any) => c.year,
					width: 100
				},
				{
					Header: ls.openingDate,
					id: "openingDate",
					show: true,
					accessor: (c: any) => {
						if (!c.mei?.openingDate) return ls.notInformed;
						
						// Check if it's a timestamp string (all digits)
						if (/^\d+$/.test(c.mei.openingDate)) {
							// Handle timestamp format (e.g., "1287540000000")
							const openingDate = new Date(parseInt(c.mei.openingDate));
							return dayjs(openingDate).format("DD/MM/YYYY");
						} else {
							// Handle string format (DD/MM/YYYY)
							return c.mei.openingDate;
						}
					},
					width: 150
				},
				{
					Header: ls.baseLimit,
					id: "baseLimit",
					show: true,
					filterable: false,
					accessor: (c: any) => {
						const currentDate = new Date();
						const base = meiBaseLimitReaisForRowYear(
							c.year,
							c.mei?.openingDate,
							currentDate.getFullYear(),
							currentDate.getMonth() + 1
						);
						return toStringCurrency(base);
					},
					exportAccessor: (c: any) => {
						const currentDate = new Date();
						return meiBaseLimitReaisForRowYear(
							c.year,
							c.mei?.openingDate,
							currentDate.getFullYear(),
							currentDate.getMonth() + 1
						);
					},
					width: 130
				},
				{
					Header: "Limite Máximo",
					id: "maxLimit",
					show: true,
					filterable: false,
					accessor: (c: any) => {
						const currentDate = new Date();
						const base = meiBaseLimitReaisForRowYear(
							c.year,
							c.mei?.openingDate,
							currentDate.getFullYear(),
							currentDate.getMonth() + 1
						);
						return toStringCurrency(base * 1.2);
					},
					exportAccessor: (c: any) => {
						const currentDate = new Date();
						const base = meiBaseLimitReaisForRowYear(
							c.year,
							c.mei?.openingDate,
							currentDate.getFullYear(),
							currentDate.getMonth() + 1
						);
						return base * 1.2;
					},
					width: 130
				},
				{
					Header: ls.value,
					id: "total",
					show: true,
					filterable: false,
					accessor: (c: any) => toStringCurrency(+c.total / 100.0),
					exportAccessor: (c: any) => +c.total / 100.0,
					width: 150
				},
				{
					Header: ls.remaining,
					id: "remaining",
					show: true,
					filterable: false,
					accessor: (c: any) => {
						const currentDate = new Date();
						const total = +c.total / 100.0; // Convert from cents
						const base = meiBaseLimitReaisForRowYear(
							c.year,
							c.mei?.openingDate,
							currentDate.getFullYear(),
							currentDate.getMonth() + 1
						);
						const maxLimitWithTolerance = base * 1.2;
						const difference = maxLimitWithTolerance - total;
						
						if (difference > 0) {
							return `+${toStringCurrency(difference)}`;
						} else if (difference < 0) {
							return `-${toStringCurrency(Math.abs(difference))}`;
						} else {
							return "R$ 0,00";
						}
					},
					exportAccessor: (c: any) => {
						const currentDate = new Date();
						const total = +c.total / 100.0; // Convert from cents
						const base = meiBaseLimitReaisForRowYear(
							c.year,
							c.mei?.openingDate,
							currentDate.getFullYear(),
							currentDate.getMonth() + 1
						);
						return base * 1.2 - total;
					},
					width: 150
				},
				{
					Header: ls.status,
					id: "status",
					show: true,
					accessor: (c: any) => ls[InvoiceAggregationStatus[c.status]],
					width: 140,
					Filter: ({ filter, onChange }: any) => {
						return (
							<select
								onChange={event => onChange(event.target.value)}
								style={{ width: "100%" }}
								value={filter ? filter.value : "all"}
							>
								<option value="">Todos</option>
								{Object.keys(InvoiceAggregationStatus)
									.filter(c => !isNaN(Number(c)))
									.map((c: any) => (
										<option key={c} value={c}>
											{ls[InvoiceAggregationStatus[c]]}
										</option>
									))}
							</select>
						);
					}
				}
		);

		return cols;
	}, [user, logisticOperators]);

	return (
		<Report
			manual
			tableRef={table}
			title="Faturamento"
			onFetchData={handleFetchData}
			data={state.invoicing}
			pages={state.pages}
			loading={state.loading.getInvoicing}
			columns={columns}
			defaultSorted={[
				{
					id: "total",
					desc: true
				}
			]}
			filterable
			getTdProps={() => {
				return {
					style: {
						display: "flex",
						alignItems: "center"
					}
				};
			}}
		/>
	);
}

export default Invoicing;
